/**
 * Sovereign Ash: Nareth Protocol
 * Fixed-Step Simulation Loop
 *
 * Deterministic fixed-timestep simulation with interpolated rendering.
 * Separates simulation logic from rendering frame rate.
 */

// ============================================================================
// Types
// ============================================================================

export interface SimulationState {
  /** Current simulation tick count */
  tick: number;
  /** Accumulated simulation time in seconds */
  time: number;
  /** Whether the simulation is running */
  running: boolean;
  /** Whether the simulation is paused */
  paused: boolean;
}

export interface SimEntity {
  id: string;
  /** Update entity state for one fixed tick */
  update(dt: number, tick: number): void;
}

export type SimStepCallback = (dt: number, tick: number) => void;
export type RenderCallback = (alpha: number, state: SimulationState) => void;

// ============================================================================
// Constants
// ============================================================================

/** Simulation tick rate in Hz */
export const SIM_TICK_RATE = 60;

/** Fixed timestep in seconds */
export const SIM_DT = 1 / SIM_TICK_RATE;

/** Fixed timestep in milliseconds */
export const SIM_DT_MS = 1000 / SIM_TICK_RATE;

/** Maximum frame time to prevent spiral of death (ms) */
export const MAX_FRAME_TIME = 250;

// ============================================================================
// Seeded PRNG (xorshift128+)
// ============================================================================

export class SeededRandom {
  private state0: bigint;
  private state1: bigint;

  constructor(seed: number) {
    // Initialize state from seed
    this.state0 = BigInt(seed) | 1n;
    this.state1 = BigInt(seed * 2654435761) | 1n;
    // Warm up
    for (let i = 0; i < 20; i++) {
      this.next();
    }
  }

  /**
   * Generate next random number in [0, 1).
   */
  next(): number {
    let s1 = this.state0;
    const s0 = this.state1;
    this.state0 = s0;
    s1 ^= s1 << 23n;
    s1 ^= s1 >> 17n;
    s1 ^= s0;
    s1 ^= s0 >> 26n;
    this.state1 = s1;

    // Convert to [0, 1)
    const result = (this.state0 + this.state1) & 0xFFFFFFFFFFFFFFFFn;
    return Number(result & 0xFFFFFFFFFFFFFn) / 0x10000000000000;
  }

  /**
   * Generate random integer in [min, max] inclusive.
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /**
   * Generate random float in [min, max).
   */
  nextFloat(min: number, max: number): number {
    return this.next() * (max - min) + min;
  }

  /**
   * Pick a random element from an array.
   */
  pick<T>(array: T[]): T {
    return array[this.nextInt(0, array.length - 1)];
  }

  /**
   * Shuffle array in place (Fisher-Yates).
   */
  shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
}

// ============================================================================
// Simulation Loop
// ============================================================================

export class SimulationLoop {
  private state: SimulationState;
  private accumulator: number = 0;
  private lastTime: number = 0;
  private rafId: number | null = null;

  private entities: Map<string, SimEntity> = new Map();
  private stepCallbacks: Set<SimStepCallback> = new Set();
  private renderCallback: RenderCallback | null = null;

  private rng: SeededRandom;

  constructor(seed: number = Date.now()) {
    this.state = {
      tick: 0,
      time: 0,
      running: false,
      paused: false,
    };
    this.rng = new SeededRandom(seed);
  }

  /**
   * Get the seeded random number generator.
   */
  getRNG(): SeededRandom {
    return this.rng;
  }

  /**
   * Get current simulation state.
   */
  getState(): Readonly<SimulationState> {
    return { ...this.state };
  }

  /**
   * Register an entity for simulation updates.
   */
  addEntity(entity: SimEntity): void {
    this.entities.set(entity.id, entity);
  }

  /**
   * Remove an entity from simulation.
   */
  removeEntity(id: string): void {
    this.entities.delete(id);
  }

  /**
   * Get an entity by ID.
   */
  getEntity(id: string): SimEntity | undefined {
    return this.entities.get(id);
  }

  /**
   * Register a callback for each simulation step.
   */
  onStep(callback: SimStepCallback): () => void {
    this.stepCallbacks.add(callback);
    return () => this.stepCallbacks.delete(callback);
  }

  /**
   * Set the render callback (called once per frame with interpolation alpha).
   */
  onRender(callback: RenderCallback): void {
    this.renderCallback = callback;
  }

  /**
   * Start the simulation loop.
   */
  start(): void {
    if (this.state.running) return;

    this.state.running = true;
    this.state.paused = false;
    this.lastTime = performance.now();
    this.accumulator = 0;

    this.loop(this.lastTime);
  }

  /**
   * Stop the simulation loop.
   */
  stop(): void {
    this.state.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Pause the simulation (rendering continues).
   */
  pause(): void {
    this.state.paused = true;
  }

  /**
   * Resume the simulation.
   */
  resume(): void {
    if (this.state.paused) {
      this.state.paused = false;
      this.lastTime = performance.now();
      this.accumulator = 0;
    }
  }

  /**
   * Toggle pause state.
   */
  togglePause(): void {
    if (this.state.paused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  /**
   * Main loop - called every animation frame.
   */
  private loop = (now: number): void => {
    if (!this.state.running) return;

    // Calculate frame time, clamped to prevent spiral of death
    const frameTime = Math.min(now - this.lastTime, MAX_FRAME_TIME);
    this.lastTime = now;

    // Accumulate time for fixed steps
    if (!this.state.paused) {
      this.accumulator += frameTime;

      // Run fixed simulation steps
      while (this.accumulator >= SIM_DT_MS) {
        this.step(SIM_DT);
        this.accumulator -= SIM_DT_MS;
      }
    }

    // Calculate interpolation alpha for smooth rendering
    const alpha = this.accumulator / SIM_DT_MS;

    // Render with interpolation
    if (this.renderCallback) {
      this.renderCallback(alpha, this.getState());
    }

    // Schedule next frame
    this.rafId = requestAnimationFrame(this.loop);
  };

  /**
   * Execute one fixed simulation step.
   */
  private step(dt: number): void {
    this.state.tick++;
    this.state.time += dt;

    // Update all entities
    for (const entity of this.entities.values()) {
      entity.update(dt, this.state.tick);
    }

    // Call step callbacks
    for (const callback of this.stepCallbacks) {
      callback(dt, this.state.tick);
    }
  }

  /**
   * Manually step the simulation (for testing/replay).
   */
  manualStep(dt: number = SIM_DT): void {
    this.step(dt);
  }

  /**
   * Reset the simulation state.
   */
  reset(seed?: number): void {
    this.stop();
    this.state = {
      tick: 0,
      time: 0,
      running: false,
      paused: false,
    };
    this.accumulator = 0;
    this.entities.clear();
    if (seed !== undefined) {
      this.rng = new SeededRandom(seed);
    }
  }

  /**
   * Get performance metrics.
   */
  getMetrics(): {
    tick: number;
    time: number;
    entityCount: number;
    accumulator: number;
  } {
    return {
      tick: this.state.tick,
      time: this.state.time,
      entityCount: this.entities.size,
      accumulator: this.accumulator,
    };
  }
}

// ============================================================================
// Interpolation Helpers
// ============================================================================

/**
 * Linear interpolation between two values.
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Interpolate a 3D vector.
 */
export function lerpVec3(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  t: number
): { x: number; y: number; z: number } {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    z: lerp(a.z, b.z, t),
  };
}

/**
 * Interpolate an angle (handles wraparound).
 */
export function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return a + diff * t;
}
